import { useEffect, useMemo, useState } from "react";
import {
  useJson, getJson, nf, plural, fmtDate, card, control, Chip, ConfidenceBadge, entityPath, countryPath, CATEGORY_BY_SLUG, Box,
} from "./shared";
import { ChoroplethMap, ColumnChart } from "./Charts";
import { StixBar } from "./Stix";

// Geopolitics › Cyber Operations (#/kb/cyber-ops): cyber incidents with who's blamed and who said
// so, from EuRepoC (cyberops.js; CC BY-NC 4.0). The filters are kept in the address (?kind=state-all
// &from=RU&against=UA&conflict=…&war=1&cat=Energy,Water&group=<KB id>&type=…&years=2022-2024&q=…); ?view=conflicts shows the armed
// conflicts instead (Cyber in Wars, its own tab until 2026-11-01; #/kb/cyber-wars still leads here).
// An incident has its own page, #/kb/cyber-op/<id>.

export const cyberOpPath = (id) => `#/kb/cyber-op/${id}`;
const KIND_PILLS = [["state-all", "State-sponsored"], ["hacktivist", "Hacktivists"], ["criminal", "Criminals"], ["other", "Others"], ["unknown", "Not attributed"], ["", "All"]];
const KIND_SHORT = { state: "State", "state-affiliated": "State-affiliated", hacktivist: "Hacktivists", criminal: "Criminals", other: "Other", unknown: "Not attributed" };
const WHO_KIND = { government: "A government", vendor: "Security researchers", reported: "The press", victim: "The victim", claimed: "The attackers' own claim", anonymous: "Unclear" };
const KEYS = ["kind", "from", "against", "conflict", "war", "cat", "group", "type", "years", "q"];
const PAGE = 30;

const note = (ui, text) => <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>{text}</span>;
const grid = (min) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`, gap: 14, alignItems: "start" });
const dim = (ui, text) => <div style={{ fontSize: ui.fs(12), color: ui.B.textDim, lineHeight: 1.5 }}>{text}</div>;

function keepInHash(state) {
  const [route, query = ""] = window.location.hash.split("?");
  const q = new URLSearchParams(query);
  for (const k of KEYS) { if (state[k]) q.set(k, state[k]); else q.delete(k); }
  const next = `${route}${q.toString() ? `?${q}` : ""}`;
  if (next !== window.location.hash) window.history.replaceState(null, "", next);
}

// EuRepoC's credit, as its licence asks.
export function Credit({ credit, ui }) {
  const { B, fs } = ui;
  if (!credit) return null;
  if (credit.hand) return <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.6 }}>Source: {credit.source}. The description is ours; the government's page is under Sources.</div>;
  return (
    <div style={{ fontSize: fs(11), color: B.textDim, lineHeight: 1.6 }}>
      Data: <a href={credit.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{credit.source}</a> v{credit.version}{credit.extracted ? `, extracted ${credit.extracted}` : ""} (doi:{credit.doi}),{" "}
      <a href={credit.licenseUrl} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{credit.license}</a>: free for non-commercial use with credit. EuRepoC's coding (who did it, who attributed it, the targets, the conflict) is shown as it is; the confidence for each attribution and the links to ATT&CK groups are ours.{credit.statements ? ` Since EuRepoC's data ends with 2024, governments' own attribution statements since 2025 (${credit.statements} so far) are kept here by hand, each linked to the government's page.` : ""}
    </div>
  );
}

// One incident in a list.
export function OpRow({ x, ui }) {
  const { B, fs, getConf } = ui;
  return (
    <div style={{ padding: "11px 0", borderTop: `1px solid ${B.border}`, display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
        <span style={{ fontSize: fs(12), color: B.textDim, whiteSpace: "nowrap", minWidth: 88 }}>{x.start ? fmtDate(x.start) : "Undated"}</span>
        <a href={cyberOpPath(x.id)} style={{ fontSize: fs(14), fontWeight: 800, color: B.text, textDecoration: "none", flex: "1 1 300px", minWidth: 0 }}>{x.name}</a>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", fontSize: fs(12), color: B.textMuted, marginLeft: 96 }}>
        <Chip ui={ui}>{KIND_SHORT[x.kind]}</Chip>
        {x.origin === "hand" && <Chip ui={ui}>Government statement</Chip>}
        {x.by.filter(b => b.name || b.country).slice(0, 3).map((b, i) => (
          <span key={i} style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
            {b.group ? <a href={entityPath(b.group)} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf(b.group.type).color}>{b.group.name}</Chip></a> : b.name ? <span style={{ color: B.text, fontWeight: 600 }}>{b.name}</span> : null}
            {b.country && <span>{b.name || b.group ? `(${b.country})` : b.country}</span>}
          </span>
        ))}
        {x.to.length > 0 && <span>→ {x.to.filter(t => t.country).map(t => t.country).filter((c, i, a) => a.indexOf(c) === i).slice(0, 4).join(", ")}{x.toMore ? " …" : ""}</span>}
        {x.types.slice(0, 3).map(t => <Chip key={t} ui={ui}>{t}</Chip>)}
      </div>
      {x.attributedBy?.basis && <div style={{ fontSize: fs(11), color: B.textDim, marginLeft: 96 }}>Attributed by {WHO_KIND[x.attributedBy.kind].toLowerCase()}{x.attributedBy.who ? `: ${x.attributedBy.who}` : ""}</div>}
    </div>
  );
}

function useFilters(params, defaults = {}) {
  const init = (k) => (params[k] !== undefined ? params[k] : defaults[k] || "");
  const [f, setF] = useState(() => Object.fromEntries(KEYS.map(k => [k, init(k)])));
  const [q, setQ] = useState(f.q);
  useEffect(() => { const t = setTimeout(() => setF(s => (s.q === q.trim() ? s : { ...s, q: q.trim() })), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { keepInHash({ ...f, kind: f.kind === (defaults.kind || "") ? "" : f.kind || "all" }); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  return { f, set, q, setQ, reset: () => { setF(Object.fromEntries(KEYS.map(k => [k, defaults[k] || ""]))); setQ(""); } };
}

// The list with its filters.
function OpsExplorer({ ui, params, defaults = {} }) {
  const { B, fs, HP, getConf } = ui;
  const { f, set, q, setQ, reset } = useFilters(params, defaults);
  const kindParam = f.kind === "all" ? "" : f.kind;
  const query = useMemo(() => {
    const u = new URLSearchParams();
    for (const k of KEYS) { const v = k === "kind" ? kindParam : f[k]; if (v) u.set(k, v); }
    return u.toString();
  }, [f, kindParam]);
  // One conflict picked: the whole conflict at a glance, above what the filters leave.
  const wars = useJson(f.conflict ? "/api/kb/cyber-ops/conflicts" : null);
  const war = wars.data?.conflicts.find(x => x.name === f.conflict);
  const [pages, setPages] = useState([]);
  const [st, setSt] = useState({ loading: true, error: "", data: null });
  useEffect(() => {
    let live = true;
    setSt(s => ({ ...s, loading: true }));
    getJson(`/api/kb/cyber-ops?${query}&limit=${PAGE}`).then(d => { if (live) { setPages([d.items]); setSt({ loading: false, error: d.error || "", data: d }); } }).catch(e => live && setSt({ loading: false, error: e.message, data: null }));
    return () => { live = false; };
  }, [query]);
  const items = pages.flat();
  const more = () => getJson(`/api/kb/cyber-ops?${query}&limit=${PAGE}&offset=${items.length}`).then(d => setPages(p => [...p, d.items])).catch(() => {});
  const d = st.data;
  const pill = (on) => ({ ...HP.navBtn, padding: "5px 11px", fontSize: fs(12), ...(on ? { color: B.text, borderColor: B.primary, background: B.primary + "14", fontWeight: 700 } : {}) });
  const years = useMemo(() => {
    if (!d?.timeline?.length) return [];
    const kinds = kindParam ? (kindParam === "state-all" ? ["state", "state-affiliated"] : [kindParam]) : null;
    const by = new Map(d.timeline.map(r => [r.year, Object.entries(r.byKind).reduce((s, [k, n]) => s + (!kinds || kinds.includes(k) ? n : 0), 0)]));
    const ys = [...by.keys()];
    const out = [];
    for (let y = Math.max(2000, Math.min(...ys)); y <= Math.max(...ys); y++) out.push({ key: String(y), value: by.get(y) || 0 });
    return out;
  }, [d, kindParam]);
  const sel = (k, label, opts) => (
    <select value={f[k]} onChange={e => set(k, e.target.value)} aria-label={label} style={{ ...control(ui), maxWidth: 260 }}>
      <option value="">{label}: any</option>
      {(opts || []).map(o => <option key={o.key} value={o.key}>{o.label || o.key} ({nf(o.count)})</option>)}
      {f[k] && !(opts || []).some(o => o.key === f[k]) && <option value={f[k]}>{f[k]}</option>}
    </select>
  );
  const filtered = KEYS.some(k => k !== "kind" && f[k]) || (f.kind || "") !== (defaults.kind || "");
  // The map: each country coloured by the side it's more often on (operations attributed to it,
  // or against it), with both counts in its tooltip; arcs for the busiest pairs.
  const flows = useMemo(() => {
    if (!d?.pairs?.length) return null;
    const by = new Map();
    for (const [side, rows] of [["from", d.facets.from], ["against", d.facets.against]]) {
      for (const c of rows) {
        if (!d.numeric[c.key]) continue;
        if (!by.has(c.key)) by.set(c.key, { iso2: c.key, numeric: d.numeric[c.key], name: c.label, from: 0, against: 0 });
        by.get(c.key)[side] = c.count;
      }
    }
    const mentions = [...by.values()].map(c => ({ ...c, kind: c.from > c.against ? "origin" : "target", plain: true, sentence: `${plural(c.from, "operation")} attributed to it, ${plural(c.against, "operation")} against it, under these filters` }));
    const arcs = d.pairs.slice(0, 25).filter(p => d.numeric[p.from] && d.numeric[p.to]).map(p => ({ from: d.numeric[p.from], to: d.numeric[p.to], pair: p, n: p.count, label: `${p.fromLabel} → ${p.toLabel}`, detail: `${plural(p.count, "operation")} under these filters · click to see them` }));
    return { mentions, arcs };
  }, [d]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} role="group" aria-label="Who did it">
        {KIND_PILLS.map(([k, l]) => <button key={l} onClick={() => set("kind", k || "all")} style={pill((f.kind || "all") === (k || "all"))} aria-pressed={(f.kind || "all") === (k || "all")}>{l}</button>)}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {sel("from", "Attributed to", d?.facets.from)}
        {sel("against", "Against", d?.facets.against)}
        {sel("conflict", "Armed conflict", d?.facets.conflicts)}
        {!f.conflict && <button onClick={() => set("war", f.war ? "" : "1")} style={pill(!!f.war)} aria-pressed={!!f.war} title="Only incidents EuRepoC ties to an armed conflict">Tied to a war{d?.facets.war !== undefined ? ` (${nf(d.facets.war)})` : ""}</button>}
        {sel("type", "Type", d?.facets.types)}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search incidents, actors, targets…" aria-label="Search incidents" style={{ ...control(ui), flex: "1 1 220px", minWidth: 0 }} />
        {f.group && <button onClick={() => set("group", "")} style={pill(true)} aria-label={`Attributed to ${d?.group?.name || f.group}; show every actor`}>Group: {d?.group?.name || "…"} ✕</button>}
        {f.cat && <button onClick={() => set("cat", "")} style={pill(true)} aria-label={`Targets in ${f.cat}; show all targets`}>Targets: {f.cat.split(",").join(", ")} ✕</button>}
        {f.years && <button onClick={() => set("years", "")} style={pill(true)} aria-label={`In ${f.years}; show all years`}>{f.years} ✕</button>}
        {filtered && <button onClick={reset} style={{ ...pill(false), marginLeft: "auto" }}>Clear filters</button>}
      </div>
      {st.error && <div style={{ color: "#dc2626", fontSize: fs(12) }}>{st.error}</div>}
      {war && (
        <Box title={war.name} ui={ui} right={<a href="#/kb/cyber-ops?view=conflicts" style={{ fontSize: fs(12), color: B.accent, textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>All conflicts →</a>}>
          <div style={{ fontSize: fs(13), color: B.textMuted, marginBottom: 8 }}>
            The whole conflict: {plural(war.total, "incident")}{war.first ? `, ${fmtDate(war.first)} to ${fmtDate(war.last)}` : ""}; {Object.entries(war.byKind).filter(([, n]) => n).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${wars.data.kindLabels[k]} ${nf(n)}`).join(" · ")}
          </div>
          {war.actors.length > 0 && <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}><span style={{ fontSize: fs(12), color: B.textDim, marginRight: 2 }}>The actors:</span>{war.actors.map(a => a.group
            ? <a key={a.key} href={entityPath(a.group)} title={`${a.group.name} in the Knowledge Base`} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf(a.group.type).color}>{a.key} {nf(a.count)} →</Chip></a>
            : <Chip key={a.key} ui={ui} color={B.textDim}>{a.key} {nf(a.count)}</Chip>)}</div>}
        </Box>
      )}
      {flows && (
        <Box title="Who acts against whom" ui={ui} right={note(ui, "the 25 busiest pairs under these filters")}>
          <ChoroplethMap mode="mentions" mentions={flows.mentions} arcs={flows.arcs} ui={ui}
            labels={{ origin: "More often the attacker's country", target: "More often the target", none: "Neither, under these filters" }}
            onSelect={(iso2, x) => set(x.kind === "origin" ? "from" : "against", iso2)} onArc={(a) => { set("from", a.pair.from); set("against", a.pair.to); }} />
          <div style={{ marginTop: 8 }}>{dim(ui, "Arcs run from the country an operation is attributed to (orange) to the country it was against (blue), thicker for more operations. Click a country to filter to the operations from it (orange) or against it (blue), or an arc for the operations between the two. Attribution is EuRepoC's, and many operations have none.")}</div>
        </Box>
      )}
      {d && years.length > 0 && (
        <Box title="By the year they began" ui={ui} right={note(ui, "click a year")}>
          <ColumnChart points={years} ui={ui} unit="incidents" onSelect={(p) => set("years", f.years === p.key ? "" : p.key)} />
        </Box>
      )}
      <Box title={st.loading && !d ? "Loading…" : `${plural(d?.total || 0, "incident")}`} ui={ui} right={note(ui, "newest first")}>
        {items.map(x => <OpRow key={x.id} x={x} ui={ui} />)}
        {!st.loading && !items.length && dim(ui, "No incidents under these filters.")}
        {items.length < (d?.total || 0) && <button onClick={more} style={{ ...control(ui), cursor: "pointer", marginTop: 10 }}>Show {Math.min(PAGE, d.total - items.length)} more</button>}
      </Box>
      <Credit credit={d?.credit} ui={ui} />
    </div>
  );
}

function Heading({ slug, ui, children }) {
  const { B, fs } = ui;
  const cat = CATEGORY_BY_SLUG[slug];
  return (
    <>
      <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: "4px 0 6px", color: B.text }}>{cat.label}</h2>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 4px", maxWidth: "110ch", lineHeight: 1.6 }}>{children || cat.blurb}</p>
    </>
  );
}

// ─── #/kb/cyber-ops ───

export function KbCyberOps({ ui, params = {} }) {
  const { B, fs, HP } = ui;
  const byConflict = params.view === "conflicts";
  const tab = (on, href, label) => (
    <a href={href} aria-current={on ? "page" : undefined} style={{ ...HP.navBtn, textDecoration: "none", padding: "6px 13px", fontSize: fs(13), ...(on ? { color: B.text, borderColor: B.primary, background: B.primary + "14", fontWeight: 700 } : {}) }}>{label}</a>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <Heading slug="cyber-ops" ui={ui}>
          Cyber incidents with political weight, 2000 to 2024, from the European Repository of Cyber Incidents (EuRepoC), and governments' own attribution statements since 2025: who's held responsible (a state's own agencies, groups affiliated with one, hacktivists, criminals), who they hit and where, and who made each attribution: a government, security researchers, the press, or the attackers themselves.
          It opens on state-sponsored operations; pick another kind of actor, a country on either side, a year, or the armed conflicts.
        </Heading>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {tab(!byConflict, "#/kb/cyber-ops", "Incidents")}
        {tab(byConflict, "#/kb/cyber-ops?view=conflicts", "By armed conflict")}
      </div>
      {byConflict ? <Conflicts ui={ui} /> : <OpsExplorer ui={ui} params={params} defaults={{ kind: "state-all" }} />}
    </div>
  );
}

// The armed conflicts, a card each; a card opens Incidents filtered to it, with every kind of actor.
function Conflicts({ ui }) {
  const { B, fs } = ui;
  const { data, error, loading } = useJson("/api/kb/cyber-ops/conflicts");
  return (
    <>
      {dim(ui, "Cyber attacks that EuRepoC ties to an armed conflict: Russia's war on Ukraine above all, and the conflicts between Israel and Hamas, Iran and Israel, the two Koreas, China and its neighbours: state agencies, groups working for them, and the hacktivists whose DDoS attacks and defacements follow every war. Pick one to see who acts from where, against whom, and how.")}
      {loading && <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>}
      {error && <div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div>}
      {data && (
        <div style={grid(300)}>
          {data.conflicts.map(x => (
            <a key={x.name} href={`#/kb/cyber-ops?kind=all&conflict=${encodeURIComponent(x.name)}`} style={{ ...card(ui), padding: "14px 16px", textDecoration: "none", color: B.text, display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: fs(15), fontWeight: 800 }}>{x.name}</span>
              <span style={{ fontSize: fs(12), color: B.textMuted }}>{plural(x.total, "incident")}{x.first ? `, ${x.first.slice(0, 4)}${x.last.slice(0, 4) !== x.first.slice(0, 4) ? `–${x.last.slice(0, 4)}` : ""}` : ""}</span>
              <span style={{ fontSize: fs(11), color: B.textDim }}>{Object.entries(x.byKind).filter(([, n]) => n).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k, n]) => `${data.kindLabels[k]} ${nf(n)}`).join(" · ")}</span>
            </a>
          ))}
        </div>
      )}
      {data && <Credit credit={data.credit} ui={ui} />}
    </>
  );
}

// ─── #/kb/cyber-op/<id> ───

export function KbCyberOp({ id, ui }) {
  const { B, fs, getConf } = ui;
  const { data, error, loading } = useJson(`/api/kb/cyber-op/${encodeURIComponent(id)}`);
  const crumb = <div style={{ fontSize: fs(12), color: B.textDim }}><a href="#/kb/cyber-ops" style={{ color: B.textDim, textDecoration: "none" }}>Cyber Operations</a> / EuRepoC incident {id}</div>;
  if (loading) return <>{crumb}<div style={{ color: B.textDim, fontSize: fs(13), marginTop: 10 }}>Loading…</div></>;
  if (error) return <>{crumb}<div style={{ color: "#dc2626", fontSize: fs(12), marginTop: 10 }}>{error}</div></>;
  const x = data;
  const fact = (k, v) => v ? <div style={{ display: "flex", gap: 10, fontSize: fs(12), padding: "4px 0" }}><span style={{ color: B.textDim, minWidth: 130 }}>{k}</span><span style={{ color: B.text, minWidth: 0 }}>{v}</span></div> : null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        {crumb}
        <h2 style={{ fontSize: fs(24), fontWeight: 900, margin: "6px 0 4px", color: B.text, lineHeight: 1.25 }}>{x.name}</h2>
        <div style={{ fontSize: fs(13), color: B.textMuted }}>{x.start ? fmtDate(x.start) : "Undated"}{x.end && x.end !== x.start ? ` to ${fmtDate(x.end)}` : ""} · {KIND_SHORT[x.kind]}{x.conflicts.length ? ` · ${x.conflicts.join("; ")}` : ""}</div>
        <StixBar apiKey={`cyber-op:${x.id}`} ui={ui} />
      </div>
      <div style={grid(340)}>
        <Box title="What happened" ui={ui} right={note(ui, "EuRepoC's description")}>
          <div style={{ fontSize: fs(13), color: B.text, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>{x.desc}</div>
        </Box>
        <Box title="The facts" ui={ui}>
          {fact("Who", x.byFull.length ? x.byFull.map((b, i) => (
            <div key={i} style={{ marginBottom: 4 }}>
              {b.group ? <a href={entityPath(b.group)} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf(b.group.type).color}>{b.group.name}</Chip></a> : null}{" "}
              <span>{b.name || "Not named"}</span>{b.cc && <> · <a href={countryPath(b.cc)} style={{ color: B.accent }}>{x.by[i]?.country || b.cc}</a></>}
              <div style={{ fontSize: fs(11), color: B.textDim }}>{KIND_SHORT[b.kind]}{b.sub ? `: ${b.sub}` : ""}</div>
            </div>
          )) : "Not attributed")}
          {fact("Against", <div>{x.to.map((t, i) => <div key={i}>{t.name || "Not named"}{t.country ? <> · {t.cc ? <a href={countryPath(t.cc)} style={{ color: B.accent }}>{t.country}</a> : t.country}</> : ""}{t.cats.length ? <span style={{ color: B.textDim }}> · {t.cats.join(", ")}</span> : ""}</div>)}</div>)}
          {fact("Type", x.types.join(", "))}
          {fact("Disclosed by", x.disclosed)}
          {fact("Impact", x.impact ? `${x.impact} (EuRepoC's index; intensity ${x.intensity})` : null)}
          {fact("Responses", x.political || x.legal ? `${x.political ? plural(x.political, "political response") : ""}${x.political && x.legal ? ", " : ""}${x.legal ? plural(x.legal, "legal response") : ""}${[...x.politicalTypes, ...x.legalTypes].length ? `: ${[...x.politicalTypes, ...x.legalTypes].slice(0, 4).join("; ")}` : ""}` : null)}
        </Box>
      </div>
      <Box title="Who said so" ui={ui} right={note(ui, "every attribution EuRepoC records; the settled one is what it holds")}>
        {x.atts.length ? x.atts.map((a, i) => (
          <div key={i} style={{ padding: "10px 0", borderTop: i ? `1px solid ${B.border}` : "none", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ minWidth: 92, fontSize: fs(12), color: B.textMuted }}>{a.date ? fmtDate(a.date) : "Undated"}</div>
            <div style={{ flex: "1 1 320px", minWidth: 0, fontSize: fs(12), lineHeight: 1.55 }}>
              <div style={{ color: B.text, fontWeight: 700 }}>{WHO_KIND[a.kind]}{a.actor || a.company ? `: ${a.actor || a.company}` : ""}{a.country ? ` (${a.country})` : ""}{a.settled && <span style={{ marginLeft: 6 }}><Chip ui={ui}>settled</Chip></span>}</div>
              <div style={{ color: B.textMuted }}>{a.by.map(b => [b.group?.name || b.name, b.country].filter(Boolean).join(", ")).filter(Boolean).join("; ") || "No one named"}</div>
              <div style={{ color: B.textDim, fontSize: fs(11) }}>{[a.basis, a.type].filter(Boolean).join(" · ")}</div>
            </div>
            <ConfidenceBadge confidence={a.confidence} ui={ui} />
          </div>
        )) : dim(ui, "No attribution: EuRepoC doesn't know who did it.")}
      </Box>
      {x.src.length > 0 && (
        <Box title="Sources" ui={ui} right={note(ui, "as EuRepoC cites them")}>
          {x.src.map(u => <div key={u} style={{ fontSize: fs(12), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "2px 0" }}><a href={u} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>{u}</a></div>)}
        </Box>
      )}
      <Credit credit={x.credit} ui={ui} />
    </div>
  );
}

// ─── On a country's page ───

export function CountryCyberOps({ ops, name, iso2, ui }) {
  const { B, fs } = ui;
  if (!ops) return null;
  const by = (o) => Object.entries(o.byKind).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${KIND_SHORT[k]} ${nf(n)}`).join(" · ");
  const more = (href, label) => <a href={href} style={{ fontSize: fs(12), color: B.accent, textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>{label} →</a>;
  return (
    <div style={grid(420)}>
      <Box title={`State-sponsored cyber operations attributed to ${name}`} jump="Cyber operations by it" ui={ui} right={ops.from.total ? more(`#/kb/cyber-ops?from=${iso2}`, "All") : null}>
        {ops.from.total ? <>
          <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 4 }}>{plural(ops.from.total, "incident")} by actors from {name}: {by(ops.from)}</div>
          {ops.from.items.map(x => <OpRow key={x.id} x={x} ui={ui} />)}
          {!ops.from.items.length && dim(ui, "None by a state or a group affiliated with one.")}
        </> : dim(ui, `EuRepoC attributes no incident to actors from ${name}.`)}
      </Box>
      <Box title={`Cyber operations against ${name}`} jump="Cyber operations against it" ui={ui} right={ops.against.total ? more(`#/kb/cyber-ops?against=${iso2}&kind=all`, "All") : null}>
        {ops.against.total ? <>
          <div style={{ fontSize: fs(12), color: B.textMuted, marginBottom: 4 }}>{plural(ops.against.total, "incident")} against targets in {name}: {by(ops.against)}</div>
          {ops.against.items.map(x => <OpRow key={x.id} x={x} ui={ui} />)}
        </> : dim(ui, `EuRepoC records no incident against ${name}.`)}
      </Box>
    </div>
  );
}
